import { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import SignatureCanvas from 'react-signature-canvas';
import i18n from '../i18n/i18n';
import { toast } from 'react-toastify';

// Components
import HeaderBar from '../components/HeaderBar';
import Avatar from '../components/Avatar';
import ContentInfo from '../components/ContentInfo';
import SignEvalButton from '../components/SignEvalButton';

// MUI Imports
import { Box, Typography, Skeleton } from '@mui/material';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

// Helpers
import { SlotData } from '../types/slotData';
import { InternData } from '../types/internData';
import { SignatureData } from '../types/signatureData';
import { getSignature, postSignature, putSignature, deleteSignature } from '../helpers/api/callSignature';
import { formatSignatureData, unformatSignatureData } from '../helpers/functions/formatSignatureData';
import { displayAndformatSlot } from '../helpers/functions/formatSlot';
import { useUserStore } from '../store/UserStore';

const Sign = () => {
    const theme = useTheme();
    const location = useLocation();
    const local = i18n.language;
    const navigate = useNavigate();
    const { user } = useUserStore();

    const { selectedSlot, signee: initialSignee } = location.state as { signee: InternData; selectedSlot: SlotData };

    const [signee, setSignee] = useState<InternData>(initialSignee);
    const [signatureData, setSignatureData] = useState<SignatureData | null>(null);
    const [isSigning, setIsSigning] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const slot = displayAndformatSlot(selectedSlot, local);
    const signatureRef = useRef<SignatureCanvas | null>(null);

    useEffect(() => {
        const fetchSignature = async () => {
            try {
                const response = await getSignature(selectedSlot.id, signee.id);
                if (response) {
                    setSignatureData(response);
                    if (response.signature) {
                        const formattedSignature = formatSignatureData(response.signature);
                        setSignatureData((prev) => (prev ? { ...prev, signature: formattedSignature } : null));
                    }
                }
            } catch (error) {
                toast.error(i18n.t('Sign.signatureFetchError'));
                console.error('Erreur lors de la récupération de la signature:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSignature();
    }, [selectedSlot.id, signee.id, signee.isPresent]);

    const handleSign = useCallback(async () => {
        if (!signatureRef.current) return;

        try {
            const signatureDataUrl = signatureRef.current.toDataURL('image/png');

            const action = signatureData?.signature
                ? putSignature(signatureData.id, unformatSignatureData(signatureDataUrl), true)
                : postSignature(selectedSlot.id.toString(), signee.id.toString(), unformatSignatureData(signatureDataUrl), true);

            await action;
            toast.success(i18n.t('Sign.confirmMessageSuccess'));
            navigate(-1);
        } catch (error) {
            toast.error(i18n.t('Sign.confirmMessageError'));
            console.error("Erreur lors de l'enregistrement de la signature:", error);
        }
    }, [signatureData, selectedSlot.id, signee.id]);

    const handleAbsent = useCallback(async () => {
        try {
            const action = signatureData ? putSignature(signatureData.id, null, false) : postSignature(selectedSlot.id.toString(), signee.id.toString(), null, false);

            await action;
            toast.success(i18n.t('Sign.absentMessageSuccess'));

            setSignee((prev) => ({ ...prev, isPresent: false }));
            navigate(-1);
        } catch (error) {
            toast.error(i18n.t('Sign.absentMessageError'));
            console.error("Erreur lors de l'absence de la signature:", error);
        }
    }, [signatureData, selectedSlot.id, signee.id]);

    const handleClearOrDeleteSign = useCallback(async () => {
        try {
            if (!signatureData) {
                signatureRef.current?.clear();
                setIsSigning(false);
                return;
            }

            await deleteSignature(signatureData.id);

            if (!signee.isPresent) {
                setSignatureData(null);
                toast.success(i18n.t('Sign.confirmDeleteAbsentMessageSuccess'));
                setSignee((prev) => ({ ...prev, isPresent: true }));
                return;
            }

            setSignatureData(null);
            signatureRef.current?.clear();
            setIsSigning(false);
            toast.success(i18n.t('Sign.confirmDeleteMessageSuccess'));
        } catch (error) {
            if (!signee.isPresent) {
                toast.error(i18n.t('Sign.confirmDeleteAbsentMessageError'));
            } else {
                toast.error(i18n.t('Sign.confirmDeleteMessageError'));
            }
            console.error('Erreur lors de la suppression de la signature:', error);
        }
    }, [signatureData, signee.isPresent]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100vh',
                alignItems: 'center',
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary
            }}
        >
            <HeaderBar />
            <Box sx={{ marginTop: '0.5em', width: '91vw', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ContentInfo flexDirection="column" margin="0 0 0.5em 0" padding="0.75em 0" customSx={{ alignItems: 'start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Box sx={{ margin: '0 0.25em 0 0.75em' }}>
                            <Avatar firstName={signee.firstname} lastName={signee.lastname} size="small" />
                        </Box>
                        <Typography sx={{ color: theme.palette.secondary.main, fontSize: '1.1em', fontWeight: '600', marginLeft: '0.75em' }}>{`${signee.firstname} ${signee.lastname}`}</Typography>
                    </Box>
                </ContentInfo>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '0.5em 0' }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: '1em', fontWeight: '600' }}>{slot}</Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10px',
                        minHeight: 0,
                        marginTop: '0.5em',
                        boxShadow: theme.palette.mode === 'light' ? '0px 3px 7px 0px rgba(0,0,0,0.15)' : 'none',
                        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#3F4C59'
                    }}
                >
                    {isLoading ? (
                        <Skeleton variant="rectangular" width="100%" height="100%" />
                    ) : !signee?.isPresent ? (
                        <>
                            <PersonOffOutlinedIcon sx={{ fontSize: '7em', opacity: 0.25 }} />
                            <Typography
                                sx={{
                                    fontSize: '2em',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    margin: '0.25em auto',
                                    opacity: 0.25
                                }}
                            >
                                {i18n.t('Sign.absent')}
                            </Typography>
                        </>
                    ) : signatureData?.signature ? (
                        <img src={signatureData.signature} alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    ) : (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <SignatureCanvas
                                penColor="black"
                                ref={signatureRef}
                                onBegin={() => setIsSigning(true)}
                                velocityFilterWeight={0}
                                canvasProps={{ style: { width: '100%', height: '100%' } }}
                            />
                            {!isSigning && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'gray' }}>
                                        <Typography sx={{ fontSize: '1.4em', fontWeight: 'bold' }}>{i18n.t('Sign.placeholder')}</Typography>
                                        <DrawOutlinedIcon sx={{ fontSize: '1.4em', ml: 1 }} />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%', padding: '1em 0 2em 0' }}>
                    <SignEvalButton size={signee.id !== user.id ? 'small' : 'medium'} backgroundColor="red" endIcon={<DeleteIcon />} onClick={handleClearOrDeleteSign}>
                        {i18n.t('Sign.clear')}
                    </SignEvalButton>
                    {signee.id !== user.id && (
                        <SignEvalButton size="small" backgroundColor="orange" endIcon={<PersonOffOutlinedIcon />} onClick={handleAbsent} disabled={!signee.isPresent}>
                            {i18n.t('Sign.absent')}
                        </SignEvalButton>
                    )}
                    <SignEvalButton size={signee.id !== user.id ? 'small' : 'medium'} backgroundColor="green" endIcon={<DrawOutlinedIcon />} onClick={handleSign} disabled={!isSigning}>
                        {i18n.t('Sign.confirm')}
                    </SignEvalButton>
                </Box>
            </Box>
        </Box>
    );
};

export default Sign;
